import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthProvider';
import { SEOHead } from '../components/seo/SEOHead';
import { listMyDynamicQR, deleteDynamicQR, DynamicQRRow, updateDynamicQR } from '../services/sbDynamicQR';
import { DynamicQREditModal } from '../components/qr/DynamicQREditModal';
import { Trash2, BarChart3, Plus, Copy, Edit } from 'lucide-react';

export const DynamicQRPage: React.FC = () => {
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [qrCodes, setQrCodes] = useState<DynamicQRRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingQR, setEditingQR] = useState<DynamicQRRow | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (!authLoading && !session) {
      navigate('/account');
    }
  }, [session, authLoading, navigate]);

  const fetchQRCodes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await listMyDynamicQR(50, 0);
      setQrCodes(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load QR codes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      fetchQRCodes();
    }
  }, [session]);

  const handleDelete = async (shortCode: string) => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) return;

    try {
      await deleteDynamicQR(shortCode);
      fetchQRCodes();
    } catch (err: any) {
      setError(err.message || 'Failed to delete QR code');
    }
  };

  const copyShortLink = (shortCode: string) => {
    const url = `${window.location.origin}/r/${shortCode}`;
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  const handleEditQR = (qr: DynamicQRRow) => {
    setEditingQR(qr);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedData: any) => {
    if (!editingQR) return;

    try {
      await updateDynamicQR(editingQR.short_code, {
        original_url: updatedData.originalUrl,
        title: updatedData.title,
        description: updatedData.description || undefined,
        expires_at: updatedData.expiresAt || undefined,
        password: updatedData.password || undefined,
        is_active: updatedData.isActive,
        customization: updatedData.customization,
      });

      // Refresh the QR codes list
      fetchQRCodes();
      setShowEditModal(false);
      setEditingQR(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update QR code');
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <>
      <SEOHead
        title="My Dynamic QR Codes | FreeQRCodeNow"
        description="Manage your dynamic QR codes with real-time analytics and tracking"
      />

      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">My Dynamic QR Codes</h1>
          <Link to="/dynamic/create">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create New
            </Button>
          </Link>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Your QR Codes</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : qrCodes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No dynamic QR codes yet. Create your first one!
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-4">Title</th>
                      <th className="py-2 pr-4">Short Code</th>
                      <th className="py-2 pr-4">Scans</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {qrCodes.map((qr) => (
                      <tr key={qr.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 pr-4 font-medium">{qr.title}</td>
                        <td className="py-3 pr-4 font-mono text-blue-600">
                          {qr.short_code}
                        </td>
                        <td className="py-3 pr-4">
                          {qr.analytics?.totalScans || 0}
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={`inline-block px-2 py-1 text-xs rounded-full ${
                              qr.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {qr.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          {new Date(qr.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => copyShortLink(qr.short_code)}
                              className="text-blue-600 hover:underline flex items-center"
                              title="Copy link"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            <Link
                              to={`/dynamic/${qr.short_code}/analytics`}
                              className="text-indigo-600 hover:underline flex items-center"
                              title="View analytics"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Link>
                            <button
                              onClick={() => handleEditQR(qr)}
                              className="text-green-600 hover:underline flex items-center"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(qr.short_code)}
                              className="text-red-600 hover:underline flex items-center"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Modal */}
        {editingQR && (
          <DynamicQREditModal
            qrCode={editingQR}
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingQR(null);
            }}
            onSave={handleSaveEdit}
          />
        )}
      </div>
    </>
  );
};


